import { z } from "zod";
import { timestampSchema } from "../common.js";
/** Server-only collections (section 8). Never readable from a client. */
export const widgetSessionSchema = z.object({
    tokenHash: z.string().length(64),
    clinicId: z.string(),
    leadId: z.string(),
    createdAt: timestampSchema,
    expiresAt: timestampSchema,
    lastSeenAt: timestampSchema.optional(),
});
export const PLATFORM_ROLES = ["superadmin", "support"];
export const platformRoleSchema = z.enum(PLATFORM_ROLES);
export const platformUserSchema = z.object({
    platformRole: platformRoleSchema,
    email: z.string(),
    mfaEnrolled: z.boolean().default(false),
    createdAt: timestampSchema,
});
export const adminAuditSchema = z.object({
    actorUid: z.string(),
    action: z.string().min(1).max(80),
    clinicId: z.string().optional(),
    leadId: z.string().optional(),
    reason: z.string().max(1000).optional(),
    at: timestampSchema,
    ip: z.string().optional(),
    data: z.record(z.string(), z.unknown()).optional(),
});
export const impersonationSessionSchema = z.object({
    actorUid: z.string(),
    clinicId: z.string(),
    startedAt: timestampSchema,
    expiresAt: timestampSchema,
    endedAt: timestampSchema.optional(),
    revealedPatientData: z.boolean().default(false),
    revealReason: z.string().max(1000).optional(),
    revealedAt: timestampSchema.optional(),
});
export const emailLogSchema = z.object({
    clinicId: z.string().optional(),
    template: z.string(),
    /** Hashed for patient mail (section 8). */
    to: z.string(),
    brevoMessageId: z.string().optional(),
    status: z.enum(["queued", "sent", "delivered", "bounced", "blocked", "spam", "failed"]),
    events: z.array(z.object({ type: z.string(), at: timestampSchema })).default([]),
    idempotencyKey: z.string().optional(),
    at: timestampSchema,
});
/** Custom claims on clinic staff tokens (section 10, 19). */
export const clinicClaimsSchema = z.object({
    clinicId: z.string(),
    role: z.enum(["owner", "admin", "pa", "doctor"]),
    doctorId: z.string().optional(),
    impersonatedBy: z.string().optional(),
    impersonationSessionId: z.string().optional(),
});
export const platformClaimsSchema = z.object({ platformRole: platformRoleSchema });
/** Staff invitation (section 10 screen 9). Lives under `clinics/{id}/invites`; the token is sent by email, never stored in clear. */
export const inviteSchema = z.object({
    email: z.string(),
    role: z.enum(["admin", "pa", "doctor"]),
    doctorId: z.string().optional(),
    invitedBy: z.string(),
    inviterName: z.string().optional(),
    tokenHash: z.string().length(64),
    createdAt: timestampSchema,
    expiresAt: timestampSchema,
    acceptedAt: timestampSchema.optional(),
    acceptedBy: z.string().optional(),
});
/** Complimentary licence granted to an email address before the clinic signs up (section 13, 19). Server-only, keyed by the SHA-256 of the lower-cased email. */
export const pendingLicenceSchema = z.object({
    email: z.string(),
    plan: z.enum(["starter", "clinic", "group"]),
    endsAt: timestampSchema.optional(),
    note: z.string().max(500).optional(),
    grantedBy: z.string(),
    createdAt: timestampSchema,
    appliedToClinic: z.string().optional(),
    appliedAt: timestampSchema.optional(),
});
/** Clinic-readable projection of a support session (section 10 screen 14, 19). Written by the server only. */
export const supportSessionViewSchema = z.object({
    actorEmail: z.string(),
    startedAt: timestampSchema,
    expiresAt: timestampSchema,
    endedAt: timestampSchema.optional(),
    revealedPatientData: z.boolean().default(false),
    revealReason: z.string().optional(),
    revealedAt: timestampSchema.optional(),
});
/** Per-clinic overrides set from the admin app (section 19 screen 8). Server-written; the clinic can read them. */
export const clinicOverridesSchema = z.object({
    limits: z
        .object({
        leadsPerMonth: z.number().int().positive().optional(),
        forms: z.number().int().positive().optional(),
        members: z.number().int().positive().optional(),
        doctors: z.number().int().positive().optional(),
        sites: z.number().int().positive().optional(),
    })
        .optional(),
    flags: z.record(z.string(), z.boolean()).optional(),
});
//# sourceMappingURL=platform.js.map